'use client';

import useSWR from 'swr';
import React, { ReactElement, useEffect, useState } from 'react';
import LeaderboardSearch from '@/components/leaderboards/LeadearboardSearch';
import RiLinkM from '~icons/ri/link-m';
import {
  datetimeInLocaleLanguageAndTimezone,
  formattedWatchTime,
  timeAgo,
  timeFormatters,
} from '@/utils/timeFormatters';
import { Range } from '@/utils/enums/Range';
import RiArrowLeftLine from '~icons/ri/arrow-left-line';
import RiArrowRightLine from '~icons/ri/arrow-right-line';
import RiLoader5Fill from '~icons/ri/loader-5-fill';
import Head from 'next/head';
import { ibm } from '@/utils/fonts/fonts';
import { HeadMetadata } from '@/components/HeadMetadata';
import { GetStaticProps, NextPage } from 'next';
import classNames from 'classnames';
import RootLayout from './RootLayout';
import { NextPageWithLayout } from './_app';

type TWatchUserData = {
  id: string;
  userId: string;
  watchTime: number;
  watchTimeMonth: number;
  createdAt: string;
  lastSeenAt: string;
  isBot: boolean;
  username: string;
};

interface Props {
  siteUrl: string;
}

const Leaderboard: NextPageWithLayout<Props> = (props) => {
  const [page, setPage] = useState<number>(0);
  const [q, setQ] = useState<string>('');
  const [rangeType, setRangeType] = useState<Range>(Range.alltime);

  const { data, error } = useSWR<TWatchUserData[]>(
    q
      ? [
          `${process.env['API_URL']}leaderboards/search?page=${
            page + 1
          }&username=${q.toLocaleLowerCase()}&type=${
            rangeType == Range.month ? 'month' : 'alltime'
          }`,
        ]
      : [
          `${process.env['API_URL']}leaderboards/top?page=${page + 1}&type=${
            rangeType == Range.month ? 'month' : 'alltime'
          }`,
        ],
    async (input: RequestInfo, init: RequestInit) => {
      const res = await fetch(input, init);
      return res.json();
    },
    {}
  );

  if (data) {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  useEffect(() => {
    setPage(0);
  }, [q, rangeType]);

  return (
    <>
      <HeadMetadata
        pageTitle='Таблица лидеров просмотра трансляций › Тиджой'
        pageDescription='Рейтинг всех пользователей по времени просмотра трансляций Тиджоя.'
        canonicalUrl={`${props.siteUrl}/leaderboard`}
        robots='index,follow'
      />
      <section className='flex flex-col gap-3 md:gap-5'>
        <LeaderboardSearch
          onSearchQChanges={setQ}
          onRangeTypeChanges={setRangeType}
        />
        {!data || error ? (
          <article className='max-w-4xl mx-auto flex justify-center'>
            <p className='flex flex-row gap-3 items-center bg-[#151515]/50 rounded-2xl w-fit px-5 p-3 text-sm border border-white/10'>
              Получение списка пользователей
              <RiLoader5Fill className='animate-spin' />
            </p>
          </article>
        ) : (
          <section className='flex flex-col gap-5'>
            <ul className='rounded-3xl bg-[#151515]/50 divide-y divide-white/5 w-full mx-auto overflow-hidden cursor-pointer'>
              {data.map((user, index) => (
                <li
                  onClick={() =>
                    window.open(
                      'https://www.twitch.tv/' + user.username,
                      '_blank'
                    )
                  }
                  key={index}
                  className='group flex flex-row items-center justify-between hover:bg-[#282828]/50 transition-colors duration-300 ease-out py-2.5 md:py-2 px-5 md:px-8'
                >
                  <div className='flex flex-row items-center gap-5 md:gap-8'>
                    {q.length == 0 ? (
                      <p
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                        className={'-tracking-[0.1em]'}
                      >
                        {index + 1 + page * 25}
                      </p>
                    ) : (
                      ''
                    )}
                    <div className='flex flex-col md:gap-0.5'>
                      <p className='line-clamp-1'>{user.username}</p>
                      <p className='text-xs md:text-sm text-zinc-400 flex flex-row gap-2 items-center'>
                        {rangeType == Range.month
                          ? formattedWatchTime(user.watchTimeMonth)
                          : formattedWatchTime(user.watchTime)}
                        <div className='hidden md:flex flex-wrap gap-2'>
                          <span className='group-hover:visible invisible'>
                            •
                          </span>
                          <span
                            style={ibm.style}
                            className='group-hover:visible invisible flex flex-row items-center gap-2'
                          >
                            Первый визит
                            <time
                              dateTime={user.createdAt}
                              title={datetimeInLocaleLanguageAndTimezone(
                                new Date(user.createdAt)
                              )}
                            >
                              {timeAgo(new Date(user.createdAt), new Date())}
                            </time>
                          </span>
                          {user.lastSeenAt !== null ? (
                            <>
                              <span className='group-hover:visible invisible'>
                                •
                              </span>
                              <span
                                style={ibm.style}
                                className='group-hover:visible invisible flex flex-row items-center gap-2'
                              >
                                Последний визит
                                <time
                                  dateTime={user.lastSeenAt}
                                  title={datetimeInLocaleLanguageAndTimezone(
                                    new Date(user.lastSeenAt)
                                  )}
                                >
                                  {timeAgo(
                                    new Date(user.lastSeenAt),
                                    new Date()
                                  )}
                                </time>
                              </span>
                            </>
                          ) : (
                            ''
                          )}
                        </div>
                      </p>
                    </div>
                  </div>
                  <RiLinkM className='text-sm md:text-base' />
                </li>
              ))}
            </ul>

            <section className='flex flex-row justify-between text-base items-center'>
              <button
                onClick={() => {
                  if (page != 0) {
                    setPage(page - 1);
                  }
                }}
                className={classNames(
                  'lex flex-row items-center justify-center text-base gap-3  bg-[#151515]/50 rounded-2xl px-5 p-2 md:px-8 border  border-white/10 w-fit',
                  {
                    'opacity-50 cursor-not-allowed': page === 0,
                    'md:hover:border-white/30 md:hover:bg-[#282828]/50':
                      page !== 0,
                  }
                )}
              >
                <RiArrowLeftLine />
              </button>
              <p>{page + 1}</p>
              <button
                onClick={() => {
                  setPage(page + 1);
                }}
                className='flex flex-row items-center justify-center text-base gap-3 md:hover:bg-[#282828]/50
                    bg-[#151515]/50 rounded-2xl px-5 p-2 md:px-8 border md:hover:border-white/30 border-white/10 w-fit'
              >
                <RiArrowRightLine />
              </button>
            </section>
          </section>
        )}
      </section>
    </>
  );
};

Leaderboard.getLayout = function getLayout(page: ReactElement) {
  return <RootLayout>{page}</RootLayout>;
};

export default Leaderboard;

export const getStaticProps: GetStaticProps<Props> = async () => {
  return {
    props: {
      siteUrl: process.env['SITE_URL'] as string,
    },
  };
};
